import {AbsoluteFill, interpolate, useCurrentFrame, Easing} from 'remotion';

/**
 * ThreeQuestionsOverlay — Voidline brand signature (Fern-style)
 *
 * Three chapter-sized questions stack one by one in the first 30s of every video.
 * Each appears with a 0.4s fade-in stagger, gold serif typography, centered.
 *
 * @example
 * <ThreeQuestionsOverlay
 *   questions={[
 *     "Why did 7 people vanish from a perfectly seaworthy ship?",
 *     "How did the rescuers find a half-eaten meal on the table?",
 *     "And what really happened in those 9 missing days?"
 *   ]}
 *   firstAppearsAtFrame={300}
 *   staggerFrames={36}
 *   durationInFrames={540}
 * />
 */

type Props = {
  questions: [string, string, string];       // exactly 3 — Fern uses 3
  firstAppearsAtFrame?: number;              // default 300 (10s @ 30fps)
  staggerFrames?: number;                    // default 36 (1.2s @ 30fps)
  durationInFrames: number;
  color?: string;                            // default '#FFD700'
  fontFamily?: string;                       // default Voidline serif
  position?: 'center' | 'lower-third';
};

export const ThreeQuestionsOverlay: React.FC<Props> = ({
  questions,
  firstAppearsAtFrame = 300,
  staggerFrames = 36,
  durationInFrames,
  color = '#FFD700',
  fontFamily = '"Cinzel", "Trajan Pro", serif',
  position = 'center',
}) => {
  const frame = useCurrentFrame();

  const fadeOutStart = durationInFrames - 30;
  const globalFadeOut = interpolate(frame, [fadeOutStart, durationInFrames], [1, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const containerStyle: React.CSSProperties = {
    position: 'absolute',
    left: '50%',
    top: position === 'center' ? '50%' : '70%',
    transform: 'translate(-50%, -50%)',
    width: '85%',
    textAlign: 'center',
    fontFamily,
    color,
    textShadow: '0 0 24px rgba(0,0,0,0.95), 0 4px 16px rgba(0,0,0,0.7)',
    opacity: globalFadeOut,
  };

  return (
    <AbsoluteFill style={{pointerEvents: 'none'}}>
      <div style={containerStyle}>
        {questions.map((q, i) => {
          const appearFrame = firstAppearsAtFrame + i * staggerFrames;
          const opacity = interpolate(
            frame,
            [appearFrame, appearFrame + 18],
            [0, 1],
            {extrapolateLeft: 'clamp', extrapolateRight: 'clamp', easing: Easing.out(Easing.cubic)}
          );
          const translateY = interpolate(
            frame,
            [appearFrame, appearFrame + 18],
            [12, 0],
            {extrapolateLeft: 'clamp', extrapolateRight: 'clamp', easing: Easing.out(Easing.cubic)}
          );
          return (
            <div
              key={i}
              style={{
                opacity,
                transform: `translateY(${translateY}px)`,
                fontSize: 52,
                lineHeight: 1.25,
                marginBottom: 28,
                letterSpacing: '0.02em',
                fontWeight: 500,
              }}
            >
              {q}
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};
